import React, { createContext, useContext, useReducer, useEffect, useCallback, ReactNode } from 'react';
import { authAPI, setInitialValidation } from '../services/api';
import { authPersistence } from '../utils/authPersistence';
import toast from 'react-hot-toast';
import { User } from '../types';
import { useInactivityTimeout } from '../hooks/useInactivityTimeout';

// Inactivity timeout configuration (in milliseconds)
// Default: 30 minutes, configurable via REACT_APP_INACTIVITY_TIMEOUT env var (in minutes)
const INACTIVITY_TIMEOUT_MINUTES = parseInt(process.env.REACT_APP_INACTIVITY_TIMEOUT || '30', 10);
const INACTIVITY_TIMEOUT = INACTIVITY_TIMEOUT_MINUTES * 60 * 1000;
const INACTIVITY_WARNING = Math.min(5 * 60 * 1000, INACTIVITY_TIMEOUT / 2); // 5 minutes or half the timeout, whichever is smaller
interface AuthState {
  user: User | null;
  token: string | null;
  loading: boolean;
  isAuthenticated: boolean;
  error: string | null;
  navigation: any[] | null;
  features: string[] | null;
  permissions: any | null;
}

interface AuthContextType extends AuthState {
  login: (identifier: string, password: string, rememberMe?: boolean) => Promise<void>;
  loginWithToken: (token: string, user: User) => Promise<void>;
  register: (userData: RegisterData) => Promise<void>;
  logout: () => void;
  updateProfile: (profileData: Partial<User['profile']>) => Promise<void>;
  updateContact: (contactData: { email?: string; phone?: string }) => Promise<void>;
  updateSettings: (settings: Partial<User['settings']>) => Promise<void>;
  updateWalletBalance: (balance: number) => void;
  refreshUserData: () => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  refreshToken: () => Promise<void>;
  refreshNavigation: () => Promise<void>;
  clearError: () => void;
}

interface RegisterData {
  username: string;
  email: string;
  phone: string;
  password: string;
  role?: 'user' | 'agent';
  referralCode?: string;
}

// Action types
type AuthAction =
  | { type: 'AUTH_START' }
  | { type: 'AUTH_SUCCESS'; payload: { user: User; token: string; navigation?: any[]; features?: string[]; permissions?: any } }
  | { type: 'AUTH_FAILURE'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'UPDATE_USER'; payload: Partial<User> }
  | { type: 'UPDATE_NAVIGATION'; payload: { navigation: any[]; features: string[]; permissions: any } }
  | { type: 'CLEAR_ERROR' };

const AUTH_TOKEN_PLACEHOLDER = 'cookie';

// Initial state - NO user is logged in by default
const initialState: AuthState = {
  user: null,
  token: null, // Do NOT read from localStorage on initial load
  loading: true,
  isAuthenticated: false,
  error: null,
  navigation: null,
  features: null,
  permissions: null,
};

// Reducer
const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'AUTH_START':
      return {
        ...state,
        loading: true,
        error: null,
      };
    case 'AUTH_SUCCESS':
      return {
        ...state,
        loading: false,
        user: action.payload.user,
        token: action.payload.token,
        navigation: action.payload.navigation || null,
        features: action.payload.features || null,
        permissions: action.payload.permissions || null,
        isAuthenticated: true,
        error: null,
      };
    case 'AUTH_FAILURE':
      return {
        ...state,
        loading: false,
        user: null,
        token: null,
        navigation: null,
        features: null,
        permissions: null,
        isAuthenticated: false,
        error: action.payload,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        navigation: null,
        features: null,
        permissions: null,
        isAuthenticated: false,
        loading: false,
        error: null,
      };
    case 'UPDATE_USER':
      return {
        ...state,
        user: state.user ? { 
          ...state.user, 
          ...action.payload,
          profile: action.payload.profile ? { ...state.user.profile, ...action.payload.profile } : state.user.profile
        } : null,
      };
    case 'UPDATE_NAVIGATION':
      return {
        ...state,
        navigation: action.payload.navigation,
        features: action.payload.features,
        permissions: action.payload.permissions,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    default:
      return state;
  }
};

// Context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  // Handle inactivity timeout
  const handleInactivityTimeout = useCallback(() => {
    if (state.isAuthenticated) {
      // Set logout flag to prevent auto-login/session restoration
      authPersistence.setExplicitLogout(true);
      
      authPersistence.clearAuthData();
      dispatch({ type: 'LOGOUT' });
      toast.error('You have been logged out due to inactivity. Please log in again.', {
        duration: 5000,
        icon: '⏰',
      });
      
      // Immediate redirect to login page
      window.location.href = '/login';
    }
  }, [state.isAuthenticated]);

  // Handle inactivity warning
  const handleInactivityWarning = useCallback(() => {
    if (state.isAuthenticated) {
      toast('You will be logged out in 5 minutes due to inactivity.', {
        duration: 10000,
        icon: '⚠️',
        style: {
          background: '#F59E0B',
          color: '#fff',
        },
      });
    }
  }, [state.isAuthenticated]);

  // Use inactivity timeout hook
  useInactivityTimeout({
    timeout: INACTIVITY_TIMEOUT,
    onTimeout: handleInactivityTimeout,
    enabled: state.isAuthenticated,
    warningTime: INACTIVITY_WARNING,
    onWarning: handleInactivityWarning,
  });

  // Initialize authentication state on app load
  useEffect(() => {
    const initAuth = async () => {
      dispatch({ type: 'AUTH_START' });
      
      try {
        if (authPersistence.isExplicitLogout()) {
          dispatch({ type: 'LOGOUT' });
          return;
        }

        const meResponse = await authAPI.getMe();
        const freshUserData = meResponse.data.data;

        const rawUser = freshUserData.user;
        const normalizedUser = rawUser
          ? { ...rawUser, id: rawUser.id || rawUser._id }
          : null;

        if (!normalizedUser || !normalizedUser.id || !normalizedUser.username || !normalizedUser.email) {
          dispatch({ type: 'LOGOUT' });
          return;
        }

        const freshNavigation = freshUserData.navigation || [];
        const freshFeatures = freshUserData.features || [];
        const freshPermissions = freshUserData.permissions || {};

        authPersistence.storeUser(normalizedUser, true);
        authPersistence.storeAuthExtras(freshNavigation, freshFeatures, freshPermissions);

        dispatch({
          type: 'AUTH_SUCCESS',
          payload: {
            user: normalizedUser,
            token: AUTH_TOKEN_PLACEHOLDER,
            navigation: freshNavigation,
            features: freshFeatures,
            permissions: freshPermissions
          },
        });
      } catch (error) {
        authPersistence.clearAuthData();
        dispatch({ type: 'LOGOUT' });
      }
    };

    initAuth();
  }, []);

  // Login
  const login = async (identifier: string, password: string, rememberMe: boolean = false) => {
    try {
      dispatch({ type: 'AUTH_START' });
      
      // Clear any explicit logout flags from previous session
      authPersistence.clearExplicitLogout();
      
      const response = await authAPI.login(identifier, password);
      
      let user = response.data.data?.user;
      
      // Fetch additional user metadata (navigation, features, permissions)
      // and the full user object from /me (login response may be minimal)
      let navigation: any[] = [], features: string[] = [], permissions: any = {};
      try {
        // Set flag to prevent "Session expired" toast during metadata fetch
        setInitialValidation(true);
        
        const metadataResponse = await authAPI.getMe();
        const metadata = metadataResponse.data.data;
        navigation = metadata.navigation || [];
        features = metadata.features || [];
        permissions = metadata.permissions || {};
        
        // Use the full user from /me if available (includes adminApprovalStatus, profile, etc.)
        if (metadata.user) {
          user = { ...metadata.user, id: metadata.user.id || metadata.user._id };
        }
        
        // Store additional auth data
        authPersistence.storeAuthExtras(navigation, features, permissions);
      } catch (metadataError) {
        // If metadata fetch fails, still login with basic user data
      } finally {
        // Reset flag after metadata fetch
        setInitialValidation(false);
      }
      
      authPersistence.storeUser(user, rememberMe);
      
      // Dispatch success with all data
      dispatch({
        type: 'AUTH_SUCCESS',
        payload: { user, token: AUTH_TOKEN_PLACEHOLDER, navigation, features, permissions },
      });
      
    } catch (error: any) {
      const message = error.response?.data?.message || 'Login failed';
      dispatch({ type: 'AUTH_FAILURE', payload: message });
      toast.error(message);
      throw error;
    }
  };

  // Login with token (for OAuth callback)
  const loginWithToken = async (token: string, user: User) => {
    try {
      authPersistence.clearExplicitLogout();
      authPersistence.storeUser(user, true);
      dispatch({
        type: 'AUTH_SUCCESS',
        payload: { user, token: token || AUTH_TOKEN_PLACEHOLDER },
      });
    } catch (error: any) {
      const message = error.response?.data?.message || 'Login failed';
      dispatch({ type: 'AUTH_FAILURE', payload: message });
      toast.error(message);
      throw error;
    }
  };

  // Register
  const register = async (userData: RegisterData) => {
    try {
      dispatch({ type: 'AUTH_START' });
      const response = await authAPI.register(userData);
      
      // Handle the new backend response structure
      const { message, approvalMessage, paymentInfo } = response.data as any;
      const user = (response.data as any)?.data?.user;
      
      // Store auth data using the new persistence system
      authPersistence.storeUser(user, true);
      
      dispatch({
        type: 'AUTH_SUCCESS',
        payload: { user, token: AUTH_TOKEN_PLACEHOLDER },
      });
      
      // Show success message only for registration
      toast.success(message || 'Registration successful!');
      
      // Show critical info for agent/admin registration (production-ready)
      if (approvalMessage) {
        toast(approvalMessage, {
          icon: 'ℹ️',
          duration: 6000,
          style: {
            background: '#3B82F6',
            color: '#fff',
          },
        });
      }
      
      // Show payment requirement (production-ready)
      if (paymentInfo?.required) {
        toast(`Payment required: ${paymentInfo.currency} ${paymentInfo.amount}`, {
          icon: '💳',
          duration: 6000,
          style: {
            background: '#F59E0B',
            color: '#fff',
          },
        });
      }
      
    } catch (error: any) {
      const message = error.response?.data?.message || 'Registration failed';
      dispatch({ type: 'AUTH_FAILURE', payload: message });
      toast.error(message);
      throw error;
    }
  };

  // Logout
  const logout = (showToast: boolean = true) => {
    // Set logout flag to prevent auto-login/session restoration
    authPersistence.setExplicitLogout(true);

    authAPI.logout().catch(() => {
    });
    
    // Clear all auth data using the new persistence system
    authPersistence.clearAuthData();
    
    // Update state
    dispatch({ type: 'LOGOUT' });
    
    // Show success toast for manual logout
    if (showToast) {
      toast.success('Logged out successfully', {
        duration: 3000,
        icon: '👋',
      });
    }
    
    // Immediate redirect to login page
    window.location.href = '/login';
  };

  // Update profile
  const updateProfile = async (profileData: Partial<User['profile']>) => {
    try {
      const response = await authAPI.updateProfile(profileData);
      const updatedUser = response.data.data.user;
      
      dispatch({
        type: 'UPDATE_USER',
        payload: updatedUser,
      });
      
      authPersistence.storeUser(updatedUser, true);
      
    } catch (error: any) {
      const message = error.response?.data?.message || 'Profile update failed';
      toast.error(message);
      throw error;
    }
  };

  // Update contact information (email, phone)
  const updateContact = async (contactData: { email?: string; phone?: string }) => {
    try {
      const response = await authAPI.updateContact(contactData);
      const updatedUser = response.data.data.user;
      
      dispatch({
        type: 'UPDATE_USER',
        payload: updatedUser,
      });
      
      authPersistence.storeUser(updatedUser, true);
      
    } catch (error: any) {
      const message = error.response?.data?.message || 'Contact update failed';
      toast.error(message);
      throw error;
    }
  };

  // Update settings
  const updateSettings = async (settings: Partial<User['settings']>) => {
    try {
      await authAPI.updateSettings({ settings });
      dispatch({
        type: 'UPDATE_USER',
        payload: { settings: { ...state.user?.settings, ...settings } as any },
      });
      toast.success('Settings updated successfully!');
    } catch (error: any) {
      const message = error.response?.data?.message || 'Settings update failed';
      toast.error(message);
      throw error;
    }
  };

  // Update wallet balance
  const updateWalletBalance = (balance: number) => {

    if (state.user) {
      const now = new Date().toISOString();
      const updatedUser = {
        ...state.user,
        wallet: { 
          ...state.user.wallet, 
          balance,
          lastUpdated: now
        }
      };

      dispatch({
        type: 'UPDATE_USER',
        payload: updatedUser,
      });
      
      authPersistence.storeUser(updatedUser, true);
    } else {
      
    }
  };

  // Change password
  const changePassword = async (currentPassword: string, newPassword: string) => {
    try {
      await authAPI.changePassword(currentPassword, newPassword);
      toast.success('Password changed successfully!');
    } catch (error: any) {
      const message = error.response?.data?.message || 'Password change failed';
      toast.error(message);
      throw error;
    }
  };

  // Refresh token
  const refreshToken = async () => {
    try {
      await authAPI.refreshToken();
    } catch (error) {

      logout(false); // Don't show toast for automatic token refresh failure
    }
  };

  // Clear error
  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  // Refresh user data from backend (including wallet balance)
  const refreshUserData = async () => {
    try {
      const response = await authAPI.getMe();
      const { user: freshUser, navigation, features, permissions } = response.data.data;
      
      if (freshUser) {
        authPersistence.storeUser(freshUser, true);
        
        // Update state with fresh user data
        dispatch({
          type: 'UPDATE_USER',
          payload: freshUser,
        });
      }
      
      // Also update navigation if provided
      if (navigation || features || permissions) {
        const navigationData: any[] = navigation || [];
        const featuresData: string[] = features || [];
        const permissionsData: any = permissions || {};

        authPersistence.storeAuthExtras(navigationData, featuresData, permissionsData);
        
        dispatch({
          type: 'UPDATE_NAVIGATION',
          payload: { navigation: navigationData, features: featuresData, permissions: permissionsData }
        });
      }
    } catch (error) {
    }
  };

  // Refresh navigation data from backend
  const refreshNavigation = async () => {
    try {
      const response = await authAPI.getMe();
      const { navigation, features, permissions } = response.data.data;
      
      // Ensure proper types with fallbacks
      const navigationData: any[] = navigation || [];
      const featuresData: string[] = features || [];
      const permissionsData: any = permissions || {};

      authPersistence.storeAuthExtras(navigationData, featuresData, permissionsData);
      
      // Update state
      dispatch({
        type: 'UPDATE_NAVIGATION',
        payload: { navigation: navigationData, features: featuresData, permissions: permissionsData }
      });
    } catch (error) {

    }
  };

  const value: AuthContextType = {
    ...state,
    login,
    loginWithToken,
    register,
    logout,
    updateProfile,
    updateContact,
    updateSettings,
    updateWalletBalance,
    refreshUserData,
    changePassword,
    refreshToken,
    refreshNavigation,
    clearError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Hook
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
